<script setup lang="ts">
import { cn } from '@pkg/ui/lib/utils';
import type { LinkType } from '@pkg/link';
import CustomLink from '@pkg/link/src/components/CustomLink.vue';
import UiButton from '@pkg/ui/components/ui/button/ButtonUi.vue';
import { BaseTile, type TileProps } from './BaseTile';

const props = defineProps<TileProps & {
  linkText?: string;
  link: LinkType;
  linkLabel?: string;
  variant?: string | TileProps['variant'];
  menuIndex?: number;
}>();
</script>

<template>
  <CustomLink :link="props.link">
    <BaseTile
      :class="cn('c-solutions-tile', {
        'c-solutions-tile--header': props.variant === 'header',
        'c-solutions-tile--center': props.variant === 'center',
      })"
      :variant="props.variant"
      :size="props.size"
      :description="props.description"
      :limit-lines="props.limitLines"
      :pre-title="props.preTitle"
      :tag="props.tag"
      :title="props.title"
      :date="props.date"
      :action-text="props.actionText"
      :image-in-content="props.imageInContent"
    >
      <template v-if="!!$slots.image" #image>
        <slot name="image" />
      </template>
      <div>
        <div v-if="props.linkText || props.linkLabel" class="c-solutions-tile__link">
          <div class="c-solutions-tile__label">
            {{ props.linkLabel }}
          </div>
          <UiButton variant="link" no-text-transform as="span">
            {{ props.linkText }}
          </UiButton>
        </div>
        <slot />
      </div>
    </BaseTile>
  </CustomLink>
</template>

<style scoped>
@import '../../../../ui/assets/css/mixins/_mixins.index.pcss';

.c-solutions-tile {
  text-align: center;

  @screen tablet-wide {
    text-align: start;
  }

  &--header {
    text-align: left;

    & .c-solutions-tile__link {
      justify-content: start;
    }

    :deep(.c-tile__description) {
      color: theme('colors.neutral.70');
      font-size: theme('fontSize.14');
    }

    :deep(.c-tile__content) {
      text-align: left;
      align-items: flex-start;
    }
  }

  &--center {
    text-align: center;

    & .c-solutions-tile__link {
      justify-content: center;
    }
  }

  &__link {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    column-gap: 4px;
    margin-right: -5px;

    @screen tablet-wide {
      flex-direction: column;
      align-items: start;

      :deep(.btn) {
        text-align: left;
      }
    }
  }

  &__label {
    @mixin body-xsmall-size;
    color: theme('colors.neutral.50');
    flex-shrink: 0;
    font-weight: theme('fontWeight.medium');
  }
}
</style>
